"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { ArrowRight, CircleX as XCircle, CircleCheck as CheckCircle2, PhoneOff, TriangleAlert as AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const workflows = [{
  id: 1,
  stage: "Inbound Phone Call",
  withoutAi: "Prospect calls after hours or during peak workload. The line rings out or hits voicemail; 80% hang up and immediately call a competitor.",
  withClearline: "Instant 24/7 AI Receptionist answers on the second ring, qualifies the caller, and books the service directly into your CRM."
}, {
  id: 2,
  stage: "AI Search Recommendation",
  withoutAi: "A prospective buyer asks ChatGPT or Perplexity for local services. Without optimized structured entity data, your brand is left out.",
  withClearline: "ClearLine GEO architecture anchors your agency inside LLM indexes so AI algorithms recommend your business first."
}, {
  id: 3,
  stage: "Speed to Lead Response",
  withoutAi: "Web inquiry sit in inbox for hours before manual follow-up. By then, the client has already signed with a faster vendor.",
  withClearline: "Automated SMS & email system reaches out within 30 seconds of form submission to capture high-intent prospects."
}, {
  id: 4,
  stage: "Reputation & Reviews",
  withoutAi: "Satisfied customers forget to leave feedback while unhappy clients express frustration publicly, damaging search rankings.",
  withClearline: "Systematic review generation triggers right after job completion, boosting positive reviews across major indexing networks."
}];
const statistics = [{
  id: 1,
  stat: "$4,200+",
  label: "ESTIMATED MONTHLY LEAK",
  subtext: "Calculated from unanswered calls and uncaptured web prospects based on average local service ticket values."
}, {
  id: 2,
  stat: "62%",
  label: "AWARDED TO FIRST RESPONDER",
  subtext: "The vast majority of modern buyers select whichever service business communicates and answers first."
}, {
  id: 3,
  stat: "< 30s",
  label: "AI RECEPTIONIST LATENCY",
  subtext: "Round-the-clock speed guarantees zero prospective customers slip away to competing service providers."
}];
export default function Missedcalls() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".reveal-header", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%"
        }
      });
      gsap.from(".workflow-row", {
        opacity: 0,
        y: 16,
        duration: 0.5,
        stagger: 0.08,
        ease: "power2.out",
        clearProps: "all",
        scrollTrigger: {
          trigger: ".workflow-container",
          start: "top 90%"
        }
      });
      gsap.from(".stat-tile", {
        opacity: 0,
        y: 36,
        duration: 0.7,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".stats-container",
          start: "top 85%"
        }
      });
    });
  }, {
    scope: containerRef
  });
  return <section id="costofmissedcalls" data-nav="dark" ref={containerRef} className="relative overflow-hidden bg-background py-24 text-foreground md:py-32">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="reveal-header mb-16 max-w-4xl">
          <div className="mb-4 inline-flex items-center gap-2">
            <Badge variant="outline" className="text-foreground inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-accent [a&]:hover:text-accent-foreground border-[var(--accent-deep-red)]/40 text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-sm !text-[rgba(129,29,29,1)] dark:aria-invalid:ring-destructive/40 bg-background">
              <AlertTriangle className="size-3.5 text-[var(--accent-deep-red)]" />
              The Revenue Leak
            </Badge>
          </div>
          <h2 className="font-serif text-3xl font-normal leading-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
            What Unanswered Calls and AI Invisibility Cost Your Business
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 text-muted-foreground md:grid-cols-2 md:gap-10 text-base leading-relaxed">
            <p>
              When a prospective customer calls your office and gets voicemail, they almost never leave a message. In local service markets, 8 out of 10 buyers immediately dial the next company on the list until someone answers live.
            </p>
            <p>
              At the same time, potential clients are asking ChatGPT and AI assistants who to hire. If your business lacks structured entity signals, search engines cite your competitors while leaving your brand completely unmentioned.
            </p>
          </div>
        </div>

        {}
        <div className="workflow-container mb-20 space-y-4">
          <div className="hidden grid-cols-12 gap-4 border-b border-border pb-3 text-xs font-mono font-semibold uppercase tracking-wider text-muted-foreground md:grid px-4">
            <div className="col-span-3">Inquiry Stage</div>
            <div className="col-span-4 text-[var(--accent-deep-red)]/90 flex items-center gap-1.5">
              <XCircle className="size-3.5" /> Without AI Automation
            </div>
            <div className="col-span-5 text-emerald-500 flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5" /> With ClearLine AI
            </div>
          </div>

          {workflows.map((item, i) => <div key={item.id} data-index={i} className="workflow-row group relative grid grid-cols-1 gap-4 border border-border bg-card opacity-100 p-5 transition-all duration-300 hover:border-border/80 hover:bg-card/80 rounded-sm md:grid-cols-12 md:items-center md:p-6">
              {}
              <div className="col-span-1 md:col-span-3">
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest block md:hidden mb-1">
                  Stage {item.id}
                </span>
                <h3 className="font-default text-lg font-semibold text-card-foreground group-hover:text-foreground">
                  {item.stage}
                </h3>
              </div>

              {}
              <div className="col-span-1 rounded bg-muted/40 p-4 md:col-span-4 md:bg-transparent md:p-0">
                <span className="mb-2 flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-[var(--accent-deep-red)] md:hidden">
                  <XCircle className="size-3.5" /> Without AI
                </span>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.withoutAi}
                </p>
              </div>

              {}
              <div className="col-span-1 rounded bg-primary/5 p-4 md:col-span-5 md:bg-transparent md:p-0">
                <span className="mb-2 flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-emerald-500 md:hidden">
                  <CheckCircle2 className="size-3.5" /> With ClearLine AI
                </span>
                <div className="flex items-start gap-3">
                  <ArrowRight className="hidden size-4 shrink-0 text-foreground/40 md:block mt-0.5 group-hover:text-foreground group-hover:translate-x-1 transition-transform" />
                  <p className="text-sm font-medium text-foreground leading-relaxed">
                    {item.withClearline}
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="stats-container mb-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {statistics.map((stat, i) => <div key={stat.id} data-index={i} className="stat-tile flex flex-col justify-between border border-border bg-card p-8 rounded-sm hover:border-border/80 transition-colors">
              <div>
                <div className="font-serif text-4xl font-normal tracking-tight text-foreground sm:text-5xl lg:text-6xl mb-3">
                  {stat.stat}
                </div>
                <div className="font-mono text-xs font-semibold uppercase tracking-wider mb-3 !text-[rgba(129,29,29,1)]">
                  {stat.label}
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-4 mt-4">
                {stat.subtext}
              </p>
            </div>)}
        </div>

        {}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border border-border bg-card/60 p-8 rounded-sm">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-serif text-xl font-normal text-foreground">
              Ready to plug the leaks in your client acquisition pipeline?
            </h3>
            <p className="text-sm text-muted-foreground">
              Get an instant visibility audit and see where your business is missing out on inbound leads.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-10 rounded-md px-6 has-[>svg]:px-4 w-full font-medium bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 sm:w-auto">
              <Link to="/PricingPlansPage">Get started now</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                Book a Call
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}